import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../utils/format';
import { useAnalytics } from '../hooks/useAnalytics';
import { useShippingOptions, usePaymentOptions, useInitiateCheckout, useValidateCart } from '../api';
import { CHECKOUT_CONSTANTS } from '@petmas/shared';

type CheckoutStep = 'information' | 'shipping' | 'payment' | 'review';

// Payment method icons
const paymentIcons: Record<string, string> = {
  'tarjeta': '💳',
  'yape': '📱',
  'plin': '📱',
  'transferencia': '🏦',
  'efectivo': '💵',
  'default': '💳',
};

function getPaymentIcon(name: string): string {
  const lowerName = name.toLowerCase();
  for (const [key, icon] of Object.entries(paymentIcons)) {
    if (lowerName.includes(key)) return icon;
  }
  return paymentIcons.default;
}

export function CheckoutPage() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState<CheckoutStep>('information');
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const { trackBeginCheckout } = useAnalytics();
  const hasTrackedCheckout = useRef(false);

  // Fetch shipping and payment options from API
  const { data: shippingOptions, isLoading: isLoadingShipping } = useShippingOptions();
  const { data: paymentOptions, isLoading: isLoadingPayment } = usePaymentOptions();
  const initiateCheckout = useInitiateCheckout();
  const validateCart = useValidateCart();

  // Form state
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [reference, setReference] = useState('');
  const [selectedShipping, setSelectedShipping] = useState<number | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<number | null>(null);

  // Set default shipping option when loaded
  useEffect(() => {
    if (shippingOptions && shippingOptions.length > 0 && selectedShipping === null) {
      setSelectedShipping(shippingOptions[0].id);
    }
  }, [shippingOptions, selectedShipping]);

  // Set default payment option when loaded
  useEffect(() => {
    if (paymentOptions && paymentOptions.length > 0 && selectedPayment === null) {
      setSelectedPayment(paymentOptions[0].id);
    }
  }, [paymentOptions, selectedPayment]);

  // Free shipping threshold
  const freeShippingThreshold = 150;
  const selectedShippingOption = shippingOptions?.find(o => o.id === selectedShipping);
  const shippingCost = subtotal >= freeShippingThreshold
    ? 0
    : selectedShippingOption?.price ?? 0;
  const total = subtotal + shippingCost;

  // Track begin checkout (only once)
  useEffect(() => {
    if (items.length > 0 && !hasTrackedCheckout.current) {
      // Note: trackBeginCheckout expects Product objects, but we have CartItems
      // In a real implementation, we'd fetch full product data or adjust the tracking
      hasTrackedCheckout.current = true;
    }
  }, [items, trackBeginCheckout, subtotal]);

  // Redirect if cart is empty
  if (items.length === 0) {
    return (
      <div className="container py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900">Tu carrito esta vacio</h1>
        <p className="mt-2 text-gray-600">
          Agrega productos antes de continuar con el checkout.
        </p>
        <Link to="/" className="mt-4 btn btn-primary inline-block">
          Explorar productos
        </Link>
      </div>
    );
  }

  const handleSubmitOrder = async () => {
    if (!selectedShipping || !selectedPayment) {
      setCheckoutError('Por favor selecciona un metodo de envio y pago');
      return;
    }

    setCheckoutError(null);

    // First validate the cart
    try {
      const validation = await validateCart.mutateAsync();
      if (!validation.valid) {
        setCheckoutError(validation.errors[0]?.message ?? 'Error de validacion del carrito');
        return;
      }
    } catch {
      setCheckoutError('Error al validar el carrito');
      return;
    }

    // Initiate checkout with Bsale
    try {
      const result = await initiateCheckout.mutateAsync({
        shippingTypeId: selectedShipping,
        paymentTypeId: selectedPayment,
        notes: reference || undefined,
      });

      // If we get a checkout URL, redirect to Bsale hosted checkout
      if (result.checkoutUrl) {
        // Store order ID for confirmation page
        globalThis.sessionStorage.setItem('pendingOrderId', result.orderId);
        // Redirect to Bsale checkout
        window.location.href = result.checkoutUrl;
      } else {
        // No checkout URL means order was created directly (rare case)
        clearCart();
        navigate('/pedidos/confirmacion', {
          state: {
            orderId: result.orderId,
            total,
          }
        });
      }
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : 'Error al procesar el pedido');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'information':
        return email && firstName && lastName && phone;
      case 'shipping':
        return address && city && district && selectedShipping !== null;
      case 'payment':
        return selectedPayment !== null && selectedPayment > 0;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    const steps: CheckoutStep[] = ['information', 'shipping', 'payment', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  const prevStep = () => {
    const steps: CheckoutStep[] = ['information', 'shipping', 'payment', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-4">
          <Link to="/" className="text-2xl font-bold text-primary">
            PetMas
          </Link>
        </div>
      </div>

      <div className="container py-8">
        {/* Steps indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {(['information', 'shipping', 'payment', 'review'] as CheckoutStep[]).map((step, index) => (
              <div key={step} className="flex items-center">
                <button
                  onClick={() => {
                    const steps: CheckoutStep[] = ['information', 'shipping', 'payment', 'review'];
                    if (steps.indexOf(step) < steps.indexOf(currentStep)) {
                      setCurrentStep(step);
                    }
                  }}
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                    currentStep === step
                      ? 'bg-primary text-white'
                      : index < (['information', 'shipping', 'payment', 'review'] as CheckoutStep[]).indexOf(currentStep)
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {index < (['information', 'shipping', 'payment', 'review'] as CheckoutStep[]).indexOf(currentStep) ? '✓' : index + 1}
                </button>
                <span className="hidden sm:block ml-2 text-sm text-gray-600">
                  {step === 'information' && 'Informacion'}
                  {step === 'shipping' && 'Envio'}
                  {step === 'payment' && 'Pago'}
                  {step === 'review' && 'Revisar'}
                </span>
                {index < CHECKOUT_CONSTANTS.SHIPPING_STEPS && (
                  <div className="w-8 sm:w-16 h-0.5 mx-2 bg-gray-200">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{
                        width: index < (['information', 'shipping', 'payment', 'review'] as CheckoutStep[]).indexOf(currentStep) ? '100%' : '0%',
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main content */}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border p-6">
              {/* Information step */}
              {currentStep === 'information' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Informacion de contacto
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Correo electronico
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input"
                        placeholder="tu@email.com"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                          Nombre
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="input"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                          Apellido
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="input"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Telefono
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="input"
                        placeholder="+51 999 999 999"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping step */}
              {currentStep === 'shipping' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Direccion de envio
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                        Direccion
                      </label>
                      <input
                        type="text"
                        id="address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        className="input"
                        placeholder="Av. Example 123"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                          Ciudad
                        </label>
                        <input
                          type="text"
                          id="city"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          className="input"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="district" className="block text-sm font-medium text-gray-700 mb-1">
                          Distrito
                        </label>
                        <input
                          type="text"
                          id="district"
                          value={district}
                          onChange={(e) => setDistrict(e.target.value)}
                          className="input"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="reference" className="block text-sm font-medium text-gray-700 mb-1">
                        Referencia (opcional)
                      </label>
                      <input
                        type="text"
                        id="reference"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        className="input"
                        placeholder="Cerca de..."
                      />
                    </div>

                    <div className="border-t pt-4 mt-6">
                      <h3 className="font-medium text-gray-900 mb-3">Metodo de envio</h3>
                      {isLoadingShipping ? (
                        <div className="animate-pulse space-y-2">
                          <div className="h-16 bg-gray-200 rounded-lg" />
                          <div className="h-16 bg-gray-200 rounded-lg" />
                        </div>
                      ) : shippingOptions && shippingOptions.length > 0 ? (
                        <div className="space-y-2">
                          {shippingOptions.map((option) => (
                            <label
                              key={option.id}
                              className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer ${
                                selectedShipping === option.id
                                  ? 'border-primary bg-primary/10'
                                  : 'hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="radio"
                                  name="shipping"
                                  checked={selectedShipping === option.id}
                                  onChange={() => setSelectedShipping(option.id)}
                                  className="text-primary focus:ring-primary"
                                />
                                <div>
                                  <p className="font-medium text-gray-900">{option.name}</p>
                                </div>
                              </div>
                              <span className="font-medium">
                                {option.price === 0 ? 'Gratis' : formatPrice(option.price)}
                              </span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500">No hay opciones de envio disponibles</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Payment step */}
              {currentStep === 'payment' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Metodo de pago
                  </h2>
                  {isLoadingPayment ? (
                    <div className="animate-pulse space-y-2">
                      <div className="h-14 bg-gray-200 rounded-lg" />
                      <div className="h-14 bg-gray-200 rounded-lg" />
                      <div className="h-14 bg-gray-200 rounded-lg" />
                    </div>
                  ) : paymentOptions && paymentOptions.length > 0 ? (
                    <div className="space-y-2">
                      {paymentOptions.map((option) => (
                        <label
                          key={option.id}
                          className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer ${
                            selectedPayment === option.id
                              ? 'border-primary bg-primary/10'
                              : 'hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="payment"
                            checked={selectedPayment === option.id}
                            onChange={() => setSelectedPayment(option.id)}
                            className="text-primary focus:ring-primary"
                          />
                          <span className="text-2xl">{getPaymentIcon(option.name)}</span>
                          <span className="font-medium text-gray-900">{option.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">No hay metodos de pago disponibles</p>
                  )}

                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Seras redirigido a una pagina segura para completar el pago.
                    </p>
                  </div>
                </div>
              )}

              {/* Review step */}
              {currentStep === 'review' && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Revisar pedido
                  </h2>

                  {/* Contact info */}
                  <div className="border-b pb-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">Contacto</h3>
                      <button
                        onClick={() => setCurrentStep('information')}
                        className="text-sm text-primary hover:text-primary-dark"
                      >
                        Editar
                      </button>
                    </div>
                    <p className="text-sm text-gray-600">{email}</p>
                    <p className="text-sm text-gray-600">{firstName} {lastName}</p>
                    <p className="text-sm text-gray-600">{phone}</p>
                  </div>

                  {/* Shipping info */}
                  <div className="border-b pb-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">Envio</h3>
                      <button
                        onClick={() => setCurrentStep('shipping')}
                        className="text-sm text-primary hover:text-primary-dark"
                      >
                        Editar
                      </button>
                    </div>
                    <p className="text-sm text-gray-600">{address}</p>
                    <p className="text-sm text-gray-600">{district}, {city}</p>
                    {reference && <p className="text-sm text-gray-600">Ref: {reference}</p>}
                    <p className="text-sm text-gray-600 mt-1">
                      {shippingOptions?.find(o => o.id === selectedShipping)?.name}
                    </p>
                  </div>

                  {/* Payment info */}
                  <div className="border-b pb-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900">Pago</h3>
                      <button
                        onClick={() => setCurrentStep('payment')}
                        className="text-sm text-primary hover:text-primary-dark"
                      >
                        Editar
                      </button>
                    </div>
                    <p className="text-sm text-gray-600">
                      {paymentOptions?.find(o => o.id === selectedPayment)?.name}
                    </p>
                  </div>

                  {/* Items */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Productos</h3>
                    <ul className="space-y-3">
                      {items.map((item) => (
                        <li key={item.variantId} className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded flex-shrink-0">
                            {item.imageUrl && (
                              <img
                                src={item.imageUrl}
                                alt={item.productName}
                                className="w-full h-full object-cover rounded"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {item.productName}
                            </p>
                            <p className="text-xs text-gray-500">
                              Cant: {item.quantity}
                            </p>
                          </div>
                          <span className="text-sm font-medium">
                            {formatPrice(item.price * item.quantity)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Error display */}
              {checkoutError && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{checkoutError}</p>
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t">
                {currentStep !== 'information' ? (
                  <button
                    onClick={prevStep}
                    className="text-primary hover:text-primary-dark font-medium"
                  >
                    ← Volver
                  </button>
                ) : (
                  <Link
                    to="/carrito"
                    className="text-primary hover:text-primary-dark font-medium"
                  >
                    ← Volver al carrito
                  </Link>
                )}

                {currentStep === 'review' ? (
                  <button
                    onClick={handleSubmitOrder}
                    disabled={initiateCheckout.isPending || validateCart.isPending}
                    className="btn btn-primary px-8"
                  >
                    {initiateCheckout.isPending || validateCart.isPending ? 'Procesando...' : 'Confirmar Pedido'}
                  </button>
                ) : (
                  <button
                    onClick={nextStep}
                    disabled={!canProceed()}
                    className="btn btn-primary px-8"
                  >
                    Continuar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Order summary sidebar */}
          <aside className="lg:w-96">
            <div className="bg-white rounded-xl shadow-sm border p-6 sticky top-24">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Resumen del Pedido
              </h2>

              {/* Items preview */}
              <ul className="space-y-3 mb-4">
                {items.slice(0, CHECKOUT_CONSTANTS.SHIPPING_STEPS).map((item) => (
                  <li key={item.variantId} className="flex items-center gap-3">
                    <div className="relative w-12 h-12 bg-gray-100 rounded flex-shrink-0">
                      {item.imageUrl && (
                        <img
                          src={item.imageUrl}
                          alt={item.productName}
                          className="w-full h-full object-cover rounded"
                        />
                      )}
                      <span className="absolute -top-1 -right-1 bg-gray-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                        {item.quantity}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 truncate">{item.productName}</p>
                    </div>
                    <span className="text-sm font-medium">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </li>
                ))}
                {items.length > CHECKOUT_CONSTANTS.SHIPPING_STEPS && (
                  <li className="text-sm text-gray-500">
                    + {items.length - CHECKOUT_CONSTANTS.SHIPPING_STEPS} productos mas
                  </li>
                )}
              </ul>

              <div className="border-t pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Envio</span>
                  <span className={shippingCost === 0 ? 'text-green-600 font-medium' : 'font-medium'}>
                    {shippingCost === 0 ? 'GRATIS' : formatPrice(shippingCost)}
                  </span>
                </div>
              </div>

              <div className="border-t mt-4 pt-4 flex justify-between text-lg font-semibold">
                <span>Total</span>
                <span>{formatPrice(total)}</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
